import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { filterMediaFormats, highlightMediaFormatMatch } from '@/lib/media-format-filter';
import type { MediaFormat } from '@/hooks/useMediaFormats';

export interface MediaFormatTypeaheadProps {
  /** Currently selected media_format_id as string (grid cells store strings) */
  value: string;
  /** Called with the selected media_format_id as string */
  onChange: (mediaFormatId: string) => void;
  /** Full list of media formats from the API */
  mediaFormats: MediaFormat[];
  /** Whether media formats are still loading */
  isLoading?: boolean;
  /** Validation error message */
  error?: string;
  /** Field label */
  label?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Called when the input loses focus (after dropdown close) */
  onBlur?: () => void;
}

export default function MediaFormatTypeahead({
  value,
  onChange,
  mediaFormats,
  isLoading = false,
  error,
  label,
  disabled = false,
  onBlur,
}: MediaFormatTypeaheadProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number }>({ left: 0, width: 0, maxHeight: 360 });

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Generate unique IDs per instance for ARIA attributes
  const instanceId = useId();
  const inputId = `media-format-typeahead-input-${instanceId}`;
  const listboxId = `media-format-typeahead-listbox-${instanceId}`;
  const getOptionId = (index: number) => `media-format-typeahead-option-${instanceId}-${index}`;

  // Resolve display name from current value (media_format_id as string)
  const selectedFormat = useMemo(
    () => {
      const numId = parseInt(value, 10);
      if (isNaN(numId)) return undefined;
      return mediaFormats.find((f) => f.id === numId);
    },
    [mediaFormats, value]
  );

  // Check if value is set but doesn't match any format (unresolved pasted text)
  const isUnresolved = value !== '' && !selectedFormat;

  // Sync input text when value changes externally
  useEffect(() => {
    if (!isOpen) {
      if (selectedFormat) {
        setInputValue(selectedFormat.name);
      } else if (value) {
        // Show the unresolved value as-is (e.g. pasted text that didn't match)
        setInputValue(value);
      } else {
        setInputValue('');
      }
    }
  }, [selectedFormat, value, isOpen]);

  // Filtered results — uses searchQuery (empty on open = show all)
  const filtered = useMemo(() => {
    return filterMediaFormats(mediaFormats, searchQuery);
  }, [mediaFormats, searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!listRef.current || !listRef.current.contains(e.target as Node))
      ) {
        setIsOpen(false);
        setInputValue(selectedFormat ? selectedFormat.name : value);
        setHighlightedIndex(-1);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [selectedFormat, value, onBlur]);

  // Close dropdown on scroll of the page (portal-rendered dropdown doesn't follow input)
  // But ignore scroll events from within the dropdown list itself
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (listRef.current && listRef.current.contains(e.target as Node)) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    const item = items[highlightedIndex];
    if (item) {
      item.scrollIntoView?.({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const updateDropdownPos = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const maxDropdownHeight = 360;
      const openAbove = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;

      if (openAbove) {
        setDropdownPos({
          bottom: window.innerHeight - rect.top + 4,
          left: rect.left,
          width: Math.max(rect.width, 200),
          maxHeight: Math.max(Math.min(maxDropdownHeight, spaceAbove - 8), 48),
        });
      } else {
        setDropdownPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 200),
          maxHeight: Math.max(Math.min(maxDropdownHeight, spaceBelow - 8), 48),
        });
      }
    }
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled || isLoading) return;
    updateDropdownPos();
    setIsOpen(true);
    setSearchQuery(''); // Show all formats on open
    setHighlightedIndex(-1);
    // Select all text so typing replaces it
    setTimeout(() => inputRef.current?.select(), 0);
  }, [disabled, isLoading, updateDropdownPos]);

  const selectFormat = useCallback(
    (format: MediaFormat) => {
      setInputValue(format.name);
      onChange(format.id.toString());
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setSearchQuery(val); // Filter as user types
    if (!isOpen) setIsOpen(true);
    setHighlightedIndex(-1);

    // If the user clears the input, clear the form value
    if (val === '') {
      onChange('');
    }
  };

  const handleFocus = () => {
    openDropdown();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
      setInputValue(selectedFormat ? selectedFormat.name : value);
      setHighlightedIndex(-1);
      onBlur?.();
    }
  };

  const handleClick = () => {
    if (!isOpen) openDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          const target = filtered[highlightedIndex];
          if (target) selectFormat(target);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setInputValue(selectedFormat ? selectedFormat.name : value);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef} className="w-full relative">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-600 mb-1"
        >
          {label}
        </label>
      )}

      <input
        id={inputId}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={
          isOpen && highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined
        }
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={isLoading ? 'Loading media formats...' : 'Search media formats...'}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled || isLoading}
        aria-invalid={error || isUnresolved ? 'true' : undefined}
        className={cn(
          'w-full px-2 py-1 text-sm border border-neutral-200 rounded bg-white',
          'placeholder:text-neutral-400',
          'focus:outline-none focus:ring-1 focus:ring-[#6B4FBB]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          (error || isUnresolved) && 'border-red-500 focus:ring-red-500'
        )}
      />

      {isOpen && createPortal(
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          className="fixed z-[9999] bg-white border border-neutral-200 rounded-md shadow-lg overflow-y-auto"
          style={{ 
            ...(dropdownPos.bottom !== undefined ? { bottom: dropdownPos.bottom } : { top: dropdownPos.top }),
            left: dropdownPos.left, 
            minWidth: dropdownPos.width, 
            width: 'max-content',
            maxHeight: dropdownPos.maxHeight,
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((format, index) => (
              <li
                key={format.id}
                id={getOptionId(index)}
                role="option"
                aria-selected={highlightedIndex === index}
                className={cn(
                  'px-3 py-2 text-sm cursor-pointer',
                  highlightedIndex === index
                    ? 'bg-[#EEE9F9]'
                    : 'hover:bg-neutral-50'
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectFormat(format);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="block whitespace-nowrap">
                  {highlightMediaFormatMatch(format.name, inputValue)}
                  {format.code && (
                    <span className="ml-1.5 text-neutral-400 text-xs">({format.code})</span>
                  )}
                </span>
              </li>
            ))
          ) : (
            <li
              role="option"
              aria-disabled="true"
              className="px-3 py-2 text-sm text-neutral-400 cursor-default"
            >
              No results found
            </li>
          )}
        </ul>,
        document.body
      )}

    </div>
  );
}
